import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  Switch,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import BrokenImageOutlinedIcon from "@mui/icons-material/BrokenImageOutlined";
import { useSnackbar } from "notistack";
import { useDispatch, useSelector } from "react-redux";
import DashboardLayout from "../../../components/layout/DashboardLayout";
import { fetchAllProducts, updateProduct } from "../../../api/products";
import { setMarket } from "../../../redux/slices/uiSlice";
import {
  calculateWb2026,
  normalizeWb2026Inputs,
  WB_2026_RULESET,
  WB_TAX_MODES,
} from "../../../features/unitEconomics/calculators/wb2026";
import {
  calculateOzon2026,
  normalizeOzon2026Inputs,
  OZON_2026_RULESET,
  OZON_TAX_MODES,
} from "../../../features/unitEconomics/calculators/ozon2026";
import {
  calculateYandexMarket2026,
  normalizeYandexMarket2026Inputs,
  YANDEX_MARKET_2026_RULESET,
  YANDEX_MARKET_TAX_MODES,
} from "../../../features/unitEconomics/calculators/yandexMarket2026";
import {
  calculateSiteOzonPay2026,
  normalizeSiteOzonPay2026Inputs,
  SITE_OZON_PAY_2026_RULESET,
  SITE_OZON_PAY_TAX_MODES,
} from "../../../features/unitEconomics/calculators/siteOzonPay2026";
import { findMinimumSellerPrice } from "../../../features/unitEconomics/priceTargets";

const money = new Intl.NumberFormat("ru-RU", {
  style: "currency",
  currency: "RUB",
  maximumFractionDigits: 0,
});
const priceMoney = new Intl.NumberFormat("ru-RU", {
  style: "currency",
  currency: "RUB",
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});
const decimal = new Intl.NumberFormat("ru-RU", { maximumFractionDigits: 2 });
const percent = new Intl.NumberFormat("ru-RU", {
  style: "percent",
  maximumFractionDigits: 1,
});

const WB_SETTINGS_FIELDS = [
  { key: "overhead", label: "Накладные расходы, ₽" },
  { key: "widthCm", label: "Ширина, см" },
  { key: "lengthCm", label: "Длина, см" },
  { key: "heightCm", label: "Высота, см" },
  { key: "buyoutPercent", label: "Выкуп, %" },
  { key: "warehouseCoefficientPercent", label: "Коэффициент склада, %" },
  { key: "dispatchHours", label: "Среднее время отгрузки, ч" },
  { key: "categoryCommissionPercent", label: "Комиссия категории, %" },
  { key: "acquiringPercent", label: "Эквайринг, %" },
  { key: "sppPercent", label: "Среднее СПП, %" },
];

const OZON_BASIC_FIELDS = [
  { key: "widthCm", label: "Ширина, см" },
  { key: "lengthCm", label: "Длина, см" },
  { key: "heightCm", label: "Высота, см" },
  {
    key: "buyoutPercent",
    label: "Выкуп, %",
    helperText: "Из отчёта Ozon; можно оставить примерный",
  },
  {
    key: "commissionPercent",
    label: "Текущая комиссия Ozon, %",
    helperText: "Обновляется ночью из Ozon API",
  },
  {
    key: "deliveryTariff",
    label: "Доставка Ozon, ₽",
    helperText: "Из калькулятора Ozon или средняя из отчёта",
  },
];

const OZON_ADVANCED_FIELDS = [
  { key: "overhead", label: "Накладные расходы, ₽" },
  {
    key: "acquiringPercent",
    label: "Эквайринг, %",
    helperText: "Обновляется ночью из Ozon API",
  },
  {
    key: "lastMileTariff",
    label: "Доставка до места выдачи, ₽",
    helperText: "Обновляется ночью из Ozon API",
  },
  {
    key: "returnTariff",
    label: "Обратная логистика, ₽",
    helperText: "Обновляется ночью из Ozon API",
  },
  { key: "storagePerUnit", label: "Хранение на единицу, ₽" },
];

const YANDEX_BASIC_FIELDS = [
  { key: "widthCm", label: "Ширина, см" },
  { key: "lengthCm", label: "Длина, см" },
  { key: "heightCm", label: "Высота, см" },
  {
    key: "buyoutPercent",
    label: "Выкуп, %",
    helperText: "Можно указать примерный процент без отчёта",
  },
  {
    key: "commissionPercent",
    label: "Размещение, %",
    helperText: "Тариф категории из кабинета Маркета",
  },
  {
    key: "paymentTransferPercent",
    label: "Перевод платежа, %",
    helperText: "1,6–3,3% в зависимости от графика выплат",
  },
];

const YANDEX_ADVANCED_FIELDS = [
  { key: "overhead", label: "Накладные расходы, ₽" },
  { key: "paymentAcceptancePerSku", label: "Приём платежа за SKU, ₽" },
];

const SITE_BASIC_FIELDS = [
  {
    key: "overhead",
    label: "Накладные расходы, ₽",
    helperText: "Расходы продавца на один заказ, кроме себестоимости",
  },
  {
    key: "acquiringPercent",
    label: "Эквайринг Ozon Pay, %",
    helperText: "По умолчанию максимум для карт — 2,2%",
  },
  {
    key: "onlineChecksPercent",
    label: "Онлайн-чеки Ozon Pay, %",
    helperText: "По умолчанию включены: 0,49%. Поставьте 0, если услуга не подключена",
  },
];

const VIEW_CONFIG = {
  wb: {
    label: "Wildberries FBS",
    expenseLabel: "Расходы WB",
    ruleset: WB_2026_RULESET,
  },
  "ozon-fbs": {
    label: "Ozon FBS",
    expenseLabel: "Расходы Ozon",
    ruleset: OZON_2026_RULESET,
    scheme: "fbs",
  },
  "ozon-fbo": {
    label: "Ozon FBO",
    expenseLabel: "Расходы Ozon",
    ruleset: OZON_2026_RULESET,
    scheme: "fbo",
  },
  "yandex-fbs": {
    label: "Яндекс Маркет FBS",
    expenseLabel: "Расходы Маркета",
    ruleset: YANDEX_MARKET_2026_RULESET,
    marketplace: "yandex",
    scheme: "fbs",
  },
  "yandex-fby": {
    label: "Яндекс Маркет FBY",
    expenseLabel: "Расходы Маркета",
    ruleset: YANDEX_MARKET_2026_RULESET,
    marketplace: "yandex",
    scheme: "fby",
  },
  site: {
    label: "Свой сайт",
    expenseLabel: "Расходы оплаты",
    ruleset: SITE_OZON_PAY_2026_RULESET,
    marketplace: "site",
  },
};

function marketplaceKey(view) {
  if (view === "wb") return "wb";
  if (view.startsWith("ozon-")) return "ozon";
  if (view === "site") return "site";
  return "yandex";
}

function marketplaceLabel(view) {
  if (view === "wb") return "Wildberries";
  if (view.startsWith("ozon-")) return "Ozon";
  if (view === "site") return "своём сайте";
  return "Яндекс Маркет";
}

function marketplaceOfferId(product, view) {
  if (view === "site") return "";
  const key = marketplaceKey(view);
  return String(
    product.marketplace_economics?.[key]?.offerId || product.offer_id || "",
  ).trim();
}

function marketplaceProductUrl(product, view) {
  const key = marketplaceKey(view);
  const value = String(
    product.marketplace_economics?.[key]?.productUrl || "",
  ).trim();
  return /^https?:\/\//i.test(value) ? value : "";
}

function withMarketplaceOfferId(current, offerId, defaultOfferId) {
  if (offerId === undefined) return current;
  const next = { ...current };
  const normalized = String(offerId || "").trim();
  const fallback = String(defaultOfferId || "").trim();
  if (normalized && normalized !== fallback) next.offerId = normalized;
  else delete next.offerId;
  return next;
}

function withMarketplaceProductUrl(current, productUrl) {
  if (productUrl === undefined) return current;
  const next = { ...current };
  const normalized = String(productUrl || "").trim();
  if (normalized) next.productUrl = normalized;
  else delete next.productUrl;
  return next;
}

function wbInputs(product) {
  const wb = product.marketplace_economics?.wb || {};
  return normalizeWb2026Inputs(wb.inputs || wb);
}

function ozonInputs(product, scheme, overrides = {}) {
  const value = product.marketplace_economics?.ozon?.[scheme] || {};
  const saved = value.inputs || value;
  const wb = wbInputs(product);
  return normalizeOzon2026Inputs(scheme, {
    // Packaging, dimensions and tax regime describe the same physical product
    // and seller, so reuse already entered WB values until Ozon overrides them.
    packaging: wb.packaging,
    overhead: wb.overhead,
    widthCm: wb.widthCm,
    lengthCm: wb.lengthCm,
    heightCm: wb.heightCm,
    taxMode: wb.taxMode,
    ...saved,
    ...overrides,
  });
}

function yandexInputs(product, scheme) {
  const value = product.marketplace_economics?.yandex?.[scheme] || {};
  const saved = value.inputs || value;
  const wb = wbInputs(product);
  return normalizeYandexMarket2026Inputs(scheme, {
    // Physical product data is shared across marketplaces until explicitly
    // overridden on the Yandex tab.
    packaging: wb.packaging,
    overhead: wb.overhead,
    widthCm: wb.widthCm,
    lengthCm: wb.lengthCm,
    heightCm: wb.heightCm,
    taxMode: wb.taxMode,
    ...saved,
  });
}

function siteInputs(product) {
  const value = product.marketplace_economics?.site || {};
  const saved = value.inputs || value;
  const wb = wbInputs(product);
  return normalizeSiteOzonPay2026Inputs({
    // Tax regime and seller overhead are shared defaults until the site
    // calculation receives its own values.
    overhead: wb.overhead,
    taxMode: wb.taxMode,
    ...saved,
  });
}

function findImage(value, depth = 0) {
  if (!value || depth > 3) return "";
  if (typeof value === "string") {
    return /^(https?:\/\/|\/uploads\/|data:image\/)/i.test(value) ? value : "";
  }
  if (Array.isArray(value)) {
    for (const entry of value) {
      const found = findImage(entry, depth + 1);
      if (found) return found;
    }
    return "";
  }
  if (typeof value === "object") {
    const preferred = [
      "url",
      "image_url",
      "imageUrl",
      "primary_image",
      "primaryImage",
      "picture",
      "image",
      "photo",
      "photos",
      "images",
      "media",
    ];
    for (const key of preferred) {
      const found = findImage(value[key], depth + 1);
      if (found) return found;
    }
  }
  return "";
}

function productImage(product) {
  return findImage(product.image_url) || findImage(product.attrs);
}

function InlineNumber({ value, onCommit, suffix, disabled, ariaLabel }) {
  return (
    <TextField
      key={value}
      defaultValue={value || ""}
      type="number"
      size="small"
      disabled={disabled}
      onKeyDown={(event) => {
        if (event.key === "Enter") event.currentTarget.blur();
      }}
      onBlur={(event) => {
        const next = Number(String(event.target.value).replace(",", "."));
        if (Number.isFinite(next) && next !== Number(value || 0))
          onCommit(Math.max(0, next));
      }}
      inputProps={{ min: 0, step: "any", "aria-label": ariaLabel }}
      InputProps={
        suffix
          ? {
              endAdornment: (
                <InputAdornment position="end">{suffix}</InputAdornment>
              ),
            }
          : undefined
      }
      sx={{ minWidth: suffix === "%" ? 105 : 120 }}
    />
  );
}

function settingsFields(view, advanced = false) {
  if (view === "wb") return WB_SETTINGS_FIELDS;
  if (view === "site") return advanced ? [] : SITE_BASIC_FIELDS;
  if (view.startsWith("yandex-")) {
    if (!advanced) {
      return view === "yandex-fbs"
        ? [
            ...YANDEX_BASIC_FIELDS,
            {
              key: "dispatchHours",
              label: "Среднее время отгрузки, ч",
              helperText:
                "Для тарифа Красота: до 28 ч — 5%, до 36 ч — 8%, дальше — 12%",
            },
          ]
        : YANDEX_BASIC_FIELDS;
    }
    return view === "yandex-fbs"
      ? [
          ...YANDEX_ADVANCED_FIELDS,
          { key: "processingPerOrder", label: "Обработка заказа, ₽" },
          { key: "courierPickupPerOrder", label: "Выезд курьера на заказ, ₽" },
        ]
      : [
          ...YANDEX_ADVANCED_FIELDS,
          { key: "storagePerUnit", label: "Хранение на проданную единицу, ₽" },
        ];
  }
  if (!advanced) return OZON_BASIC_FIELDS;
  if (view === "ozon-fbo") {
    return [
      ...OZON_ADVANCED_FIELDS,
      { key: "nonLocalPercent", label: "Наценка за нелокальную продажу, %" },
      { key: "acceptancePerUnit", label: "Поштучная приёмка, ₽" },
    ];
  }
  return [
    ...OZON_ADVANCED_FIELDS,
    { key: "processingPerOrder", label: "Обработка отправления, ₽" },
  ];
}

function SettingsDialog({ state, onClose, onSave, saving }) {
  const [inputs, setInputs] = useState(() => state?.inputs || {});
  const [offerId, setOfferId] = useState(() => state?.offerId || "");
  const [productUrl, setProductUrl] = useState(() => state?.productUrl || "");
  const [showAdvanced, setShowAdvanced] = useState(false);
  useEffect(() => {
    setInputs(state?.inputs || {});
    setOfferId(state?.offerId || "");
    setProductUrl(state?.productUrl || "");
    setShowAdvanced(false);
  }, [state]);
  if (!state) return null;

  const isWb = state.view === "wb";
  const isYandex = state.view.startsWith("yandex-");
  const isSite = state.view === "site";
  const config = VIEW_CONFIG[state.view];
  const invalidBuyout =
    !isSite &&
    (!Number.isFinite(Number(inputs.buyoutPercent)) ||
      Number(inputs.buyoutPercent) <= 0);
  const invalidProductUrl =
    Boolean(productUrl.trim()) && !/^https?:\/\//i.test(productUrl.trim());
  return (
    <Dialog open maxWidth="md" fullWidth onClose={saving ? undefined : onClose}>
      <DialogTitle>
        Параметры {config.label} · {state.product.title}
      </DialogTitle>
      <DialogContent dividers>
        <Alert severity="info" sx={{ mb: 2 }}>
          {isWb
            ? `Производные поля считаются автоматически по ${config.ruleset.label}. Здесь только исходные допущения. Себестоимость в таблице уже включает упаковку.`
            : isYandex
              ? "Отчёт не нужен: укажите размеры, примерный выкуп и тариф размещения категории. Себестоимость в таблице уже включает упаковку. Среднюю и последнюю милю Маркет считает автоматически по габаритам."
              : isSite
                ? "Доставку оплачивает покупатель, поэтому в расходы продавца она не входит. В модели: себестоимость, накладные расходы, эквайринг Ozon Pay, при необходимости онлайн-чеки, налоги и реклама."
                : "Цена, комиссия, эквайринг, последняя миля и возврат обновляются ночью из Ozon API. Вручную заполните размеры, примерный выкуп и расчётную доставку Ozon для вашего маршрута. Себестоимость в таблице уже включает упаковку."}
        </Alert>
        {!isSite && (
          <TextField
            fullWidth
            size="small"
            label={`Артикул на ${marketplaceLabel(state.view)}`}
            value={offerId}
            onChange={(event) => setOfferId(event.target.value)}
            helperText={`Используется для сопоставления с API${isWb ? "" : isYandex ? "; один для FBS и FBY" : "; один для FBS и FBO"}. По умолчанию: ${state.product.offer_id || "не задан"}`}
            sx={{ mb: 2 }}
          />
        )}
        <TextField
          fullWidth
          size="small"
          type="url"
          label={
            isSite
              ? "Ссылка на товар на сайте"
              : `Ссылка на товар в ${marketplaceLabel(state.view)}`
          }
          value={productUrl}
          onChange={(event) => setProductUrl(event.target.value)}
          error={invalidProductUrl}
          helperText={
            invalidProductUrl
              ? "Ссылка должна начинаться с http:// или https://"
              : isSite
                ? "Необязательно: ссылка для быстрого перехода к карточке или её редактированию."
                : `Вставьте ссылку на редактирование товара; она общая для ${isWb ? "Wildberries" : isYandex ? "FBS и FBY" : "FBS и FBO"}.`
          }
          sx={{ mb: 2 }}
        />
        {isYandex && (
          <Box sx={{ mb: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={Boolean(inputs.beautyReducedTariff)}
                  onChange={(event) =>
                    setInputs((current) => ({
                      ...current,
                      beautyReducedTariff: event.target.checked,
                    }))
                  }
                />
              }
              label="Сниженный тариф «Красота»"
            />
            <Typography
              variant="caption"
              display="block"
              color="text.secondary"
            >
              {inputs.beautyReducedTariff
                ? state.view === "yandex-fby" ||
                  Number(inputs.dispatchHours) <= 28
                  ? "В расчёте применяется размещение 5%."
                  : Number(inputs.dispatchHours) <= 36
                    ? "В расчёте применяется размещение 8%."
                    : "В расчёте применяется размещение 12%."
                : "Выключено — используется обычная комиссия категории."}
            </Typography>
          </Box>
        )}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "1fr 1fr",
              md: "1fr 1fr 1fr",
            },
            gap: 2,
          }}
        >
          {settingsFields(state.view).map((field) => (
            <TextField
              key={field.key}
              label={field.label}
              type="number"
              value={inputs[field.key] ?? ""}
              onChange={(event) =>
                setInputs((current) => ({
                  ...current,
                  [field.key]: event.target.value,
                }))
              }
              inputProps={{
                min: field.key === "buyoutPercent" ? 1 : 0,
                step: "any",
              }}
              error={field.key === "buyoutPercent" && invalidBuyout}
              helperText={
                field.key === "buyoutPercent" && invalidBuyout
                  ? "Выкуп должен быть больше 0%, иначе прибыль на одну продажу не рассчитывается"
                  : field.helperText
              }
              size="small"
            />
          ))}
          <TextField
            select
            SelectProps={{ native: true }}
            label="Налоговый режим"
            value={inputs.taxMode || "usn6"}
            onChange={(event) =>
              setInputs((current) => ({
                ...current,
                taxMode: event.target.value,
              }))
            }
            size="small"
          >
            {(isWb
              ? WB_TAX_MODES
              : isYandex
                ? YANDEX_MARKET_TAX_MODES
                : isSite
                  ? SITE_OZON_PAY_TAX_MODES
                  : OZON_TAX_MODES
            ).map((mode) => (
              <option key={mode.value} value={mode.value}>
                {mode.label}
              </option>
            ))}
          </TextField>
        </Box>
        {!isWb && !isSite && (
          <Box sx={{ mt: 2 }}>
            <Button
              size="small"
              onClick={() => setShowAdvanced((value) => !value)}
            >
              {showAdvanced
                ? "Скрыть дополнительные параметры"
                : "Показать дополнительные параметры"}
            </Button>
            {showAdvanced && (
              <Box
                sx={{
                  mt: 2,
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    sm: "1fr 1fr",
                    md: "1fr 1fr 1fr",
                  },
                  gap: 2,
                }}
              >
                {settingsFields(state.view, true).map((field) => (
                  <TextField
                    key={field.key}
                    label={field.label}
                    type="number"
                    value={inputs[field.key] ?? ""}
                    onChange={(event) =>
                      setInputs((current) => ({
                        ...current,
                        [field.key]: event.target.value,
                      }))
                    }
                    inputProps={{ min: 0, step: "any" }}
                    helperText={field.helperText}
                    size="small"
                  />
                ))}
              </Box>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Отмена
        </Button>
        <Button
          variant="contained"
          onClick={() => onSave(inputs, offerId, productUrl)}
          disabled={saving || invalidBuyout || invalidProductUrl}
        >
          {saving ? "Сохраняю…" : "Сохранить параметры"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function UnitEconomicsPage() {
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();
  const market = useSelector((state) => state.ui.market);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [view, setView] = useState("wb");
  const [savingId, setSavingId] = useState(null);
  const [settings, setSettings] = useState(null);
  const viewConfig = VIEW_CONFIG[view];

  useEffect(() => {
    let active = true;
    setLoading(true);
    fetchAllProducts()
      .then((rows) => {
        if (active) setProducts(rows);
      })
      .catch((error) => {
        console.error(error);
        enqueueSnackbar("Не удалось загрузить товары", { variant: "error" });
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [enqueueSnackbar]);

  const rows = useMemo(() => {
    const needle = query.trim().toLocaleLowerCase("ru");
    return products
      .filter((product) => {
        if (!needle) return true;
        return [
          product.title,
          product.article,
          product.offer_id,
          product.sku,
          product.marketplace_economics?.wb?.offerId,
          product.marketplace_economics?.ozon?.offerId,
          product.marketplace_economics?.yandex?.offerId,
        ]
          .filter(Boolean)
          .some((value) =>
            String(value).toLocaleLowerCase("ru").includes(needle),
          );
      })
      .map((product) => {
        const isWb = view === "wb";
        const config = VIEW_CONFIG[view];
        const scheme = config.scheme;
        const isYandex = config.marketplace === "yandex";
        const isSite = config.marketplace === "site";
        const inputs = isWb
          ? wbInputs(product)
          : isYandex
            ? yandexInputs(product, scheme)
            : isSite
              ? siteInputs(product)
              : ozonInputs(product, scheme);
        const calculateMetrics = (nextInputs) =>
          isWb
            ? calculateWb2026(nextInputs, product.cost_price)
            : isYandex
              ? calculateYandexMarket2026(
                  scheme,
                  nextInputs,
                  product.cost_price,
                )
              : isSite
                ? calculateSiteOzonPay2026(nextInputs, product.cost_price)
                : calculateOzon2026(scheme, nextInputs, product.cost_price);
        const metrics = calculateMetrics(inputs);
        const profitAtPrice = (price) => {
          // Re-normalize legacy Ozon tier fields for every candidate price.
          // Current records with a single commissionPercent remain unchanged.
          const candidateInputs =
            isWb || isYandex || isSite
              ? { ...inputs, price }
              : ozonInputs(product, scheme, { price });
          return calculateMetrics(candidateInputs).profit;
        };
        // Yandex changes the low-price tariff above 300 RUB and caps its 5%
        // last-mile charge at 1000 RUB (reached at a 20,000 RUB price).
        const priceBreakpoints = isYandex
          ? [300, 20_000]
          : isWb || isSite
            ? []
            : [100, 300];
        const priceSearchOptions = { breakpoints: priceBreakpoints };
        return {
          product,
          inputs,
          metrics,
          priceTargets: {
            breakEven: findMinimumSellerPrice(
              profitAtPrice,
              0,
              priceSearchOptions,
            ),
            profitX1: findMinimumSellerPrice(
              profitAtPrice,
              Number(product.cost_price) || 0,
              priceSearchOptions,
            ),
          },
          image: productImage(product),
        };
      });
  }, [products, query, view]);

  async function persist(product, patch) {
    setSavingId(product.documentId);
    try {
      await updateProduct(product.documentId, patch);
      setProducts((current) =>
        current.map((entry) =>
          entry.documentId === product.documentId
            ? { ...entry, ...patch }
            : entry,
        ),
      );
      return true;
    } catch (error) {
      console.error(error);
      enqueueSnackbar(`Не удалось сохранить «${product.title}»`, {
        variant: "error",
      });
      return false;
    } finally {
      setSavingId(null);
    }
  }

  async function persistWbInputs(product, patch, offerId, productUrl) {
    const nextInputs = normalizeWb2026Inputs({
      ...wbInputs(product),
      ...patch,
    });
    const currentEconomics = product.marketplace_economics || {};
    const currentWb = currentEconomics.wb || {};
    return persist(product, {
      marketplace_economics: {
        ...currentEconomics,
        wb: withMarketplaceProductUrl(
          withMarketplaceOfferId(
            { ...currentWb, rulesetId: WB_2026_RULESET.id, inputs: nextInputs },
            offerId,
            product.offer_id,
          ),
          productUrl,
        ),
      },
    });
  }

  async function persistOzonInputs(
    product,
    scheme,
    patch,
    offerId,
    productUrl,
  ) {
    const nextInputs = normalizeOzon2026Inputs(scheme, {
      ...ozonInputs(product, scheme),
      ...patch,
    });
    const currentEconomics = product.marketplace_economics || {};
    const currentOzon = currentEconomics.ozon || {};
    return persist(product, {
      marketplace_economics: {
        ...currentEconomics,
        ozon: {
          ...withMarketplaceProductUrl(
            withMarketplaceOfferId(currentOzon, offerId, product.offer_id),
            productUrl,
          ),
          rulesetId: OZON_2026_RULESET.id,
          [scheme]: {
            ...currentOzon[scheme],
            rulesetId: OZON_2026_RULESET.id,
            inputs: nextInputs,
          },
        },
      },
    });
  }

  async function persistYandexInputs(
    product,
    scheme,
    patch,
    offerId,
    productUrl,
  ) {
    const nextInputs = normalizeYandexMarket2026Inputs(scheme, {
      ...yandexInputs(product, scheme),
      ...patch,
    });
    const currentEconomics = product.marketplace_economics || {};
    const currentYandex = currentEconomics.yandex || {};
    return persist(product, {
      marketplace_economics: {
        ...currentEconomics,
        yandex: {
          ...withMarketplaceProductUrl(
            withMarketplaceOfferId(currentYandex, offerId, product.offer_id),
            productUrl,
          ),
          rulesetId: YANDEX_MARKET_2026_RULESET.id,
          [scheme]: {
            ...currentYandex[scheme],
            rulesetId: YANDEX_MARKET_2026_RULESET.id,
            inputs: nextInputs,
          },
        },
      },
    });
  }

  async function persistSiteInputs(product, patch, _offerId, productUrl) {
    const nextInputs = normalizeSiteOzonPay2026Inputs({
      ...siteInputs(product),
      ...patch,
    });
    const currentEconomics = product.marketplace_economics || {};
    const currentSite = currentEconomics.site || {};
    return persist(product, {
      marketplace_economics: {
        ...currentEconomics,
        site: withMarketplaceProductUrl(
          {
            ...currentSite,
            rulesetId: SITE_OZON_PAY_2026_RULESET.id,
            inputs: nextInputs,
          },
          productUrl,
        ),
      },
    });
  }

  function persistInputs(
    product,
    patch,
    targetView = view,
    offerId,
    productUrl,
  ) {
    if (targetView === "wb")
      return persistWbInputs(product, patch, offerId, productUrl);
    const config = VIEW_CONFIG[targetView];
    if (config.marketplace === "site") {
      return persistSiteInputs(product, patch, offerId, productUrl);
    }
    if (config.marketplace === "yandex") {
      return persistYandexInputs(
        product,
        config.scheme,
        patch,
        offerId,
        productUrl,
      );
    }
    return persistOzonInputs(
      product,
      config.scheme,
      patch,
      offerId,
      productUrl,
    );
  }

  return (
    <DashboardLayout
      market={market}
      onSelectMarket={(nextMarket) => dispatch(setMarket(nextMarket))}
    >
      <Stack spacing={2} sx={{ minWidth: 0 }}>
        <Paper variant="outlined" sx={{ overflow: "hidden" }}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "stretch", md: "center" }}
            gap={2}
            sx={{ p: 2, pb: 1 }}
          >
            <Box>
              <Typography variant="h5" fontWeight={750}>
                Unit-экономика товаров
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {viewConfig.ruleset.label} · {products.length} товаров
              </Typography>
            </Box>
            <TextField
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              size="small"
              placeholder="Название, артикул или SKU"
              sx={{ width: { xs: "100%", md: 340 } }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchRoundedIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </Stack>
          <Tabs
            value={view}
            onChange={(_event, nextView) => setView(nextView)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ px: 1 }}
          >
            {Object.entries(VIEW_CONFIG).map(([key, config]) => (
              <Tab key={key} value={key} label={config.label} />
            ))}
          </Tabs>
        </Paper>

        <Paper variant="outlined" sx={{ overflow: "hidden" }}>
          {loading ? (
            <Box sx={{ p: 6, textAlign: "center" }}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <TableContainer sx={{ maxHeight: "calc(100vh - 255px)" }}>
              <Table stickyHeader size="small" sx={{ minWidth: 1510 }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 68 }}>Фото</TableCell>
                    <TableCell sx={{ minWidth: 260 }}>Товар</TableCell>
                    <TableCell>Цена продавца</TableCell>
                    <TableCell>Себестоимость с упаковкой</TableCell>
                    <TableCell align="right">
                      {viewConfig.expenseLabel}
                    </TableCell>
                    <TableCell align="right">Логистика</TableCell>
                    <TableCell align="right">Прибыль / шт.</TableCell>
                    <TableCell align="right">Маржа</TableCell>
                    <TableCell>Реклама</TableCell>
                    <TableCell align="right">Прибыль с рекламой</TableCell>
                    <TableCell align="right">Итоговая маржа</TableCell>
                    <TableCell align="center">Параметры</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map(
                    ({ product, inputs, metrics, priceTargets, image }) => {
                      const saving = savingId === product.documentId;
                      const marketplaceExpenses =
                        view === "wb"
                          ? metrics.logistics +
                            metrics.paidAcceptance +
                            metrics.commission +
                            metrics.acquiring +
                            metrics.dispatchCommission +
                            metrics.tax
                          : metrics.marketplaceExpenses;
                      return (
                        <TableRow key={product.documentId || product.id} hover>
                          <TableCell>
                            <Avatar
                              variant="rounded"
                              src={image || undefined}
                              alt={product.title}
                              sx={{
                                width: 46,
                                height: 46,
                                bgcolor: "grey.100",
                                color: "grey.500",
                              }}
                            >
                              <BrokenImageOutlinedIcon fontSize="small" />
                            </Avatar>
                          </TableCell>
                          <TableCell>
                            <Typography
                              variant="body2"
                              fontWeight={650}
                              noWrap
                              sx={{ maxWidth: 300 }}
                            >
                              {product.title}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {product.article ||
                                product.offer_id ||
                                product.sku ||
                                "Без артикула"}
                            </Typography>
                            {marketplaceOfferId(product, view) && (
                              <Typography
                                variant="caption"
                                display="block"
                                color="text.secondary"
                              >
                                API: {marketplaceOfferId(product, view)}
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell>
                            <InlineNumber
                              value={inputs.price}
                              suffix="₽"
                              disabled={saving}
                              ariaLabel={`Цена продавца ${product.title}`}
                              onCommit={(price) =>
                                persistInputs(product, { price })
                              }
                            />
                            <Typography
                              variant="caption"
                              display="block"
                              color="text.secondary"
                            >
                              {view === "wb"
                                ? `покупатель: ${money.format(metrics.buyerPrice)}`
                                : view === "site"
                                  ? "доставка: за счёт клиента"
                                  : `объём: ${decimal.format(metrics.volumeLiters)} л`}
                            </Typography>
                            <Tooltip
                              arrow
                              title="Б/У — цена безубыточности до рекламы; ×1 — цена, при которой прибыль до рекламы равна себестоимости товара"
                            >
                              <Typography
                                variant="caption"
                                display="block"
                                color="text.secondary"
                                noWrap
                              >
                                Б/У:{" "}
                                {priceTargets.breakEven == null
                                  ? "—"
                                  : priceMoney.format(priceTargets.breakEven)}
                                {" · ×1: "}
                                {priceTargets.profitX1 == null
                                  ? "—"
                                  : priceMoney.format(priceTargets.profitX1)}
                              </Typography>
                            </Tooltip>
                          </TableCell>
                          <TableCell>
                            <InlineNumber
                              value={product.cost_price}
                              suffix="₽"
                              disabled={saving}
                              ariaLabel={`Себестоимость с упаковкой ${product.title}`}
                              onCommit={(cost_price) =>
                                persist(product, { cost_price })
                              }
                            />
                            <Typography
                              variant="caption"
                              display="block"
                              color="text.secondary"
                            >
                              в расчёте: {money.format(metrics.totalCost)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            {money.format(marketplaceExpenses)}
                          </TableCell>
                          <TableCell align="right">
                            {money.format(metrics.logistics)}
                          </TableCell>
                          <TableCell align="right">
                            <Typography
                              color={
                                metrics.profit < 0
                                  ? "error.main"
                                  : "success.main"
                              }
                              fontWeight={700}
                            >
                              {money.format(metrics.profit)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            {percent.format(metrics.margin)}
                          </TableCell>
                          <TableCell>
                            <InlineNumber
                              value={inputs.advertisingPercent}
                              suffix="%"
                              disabled={saving}
                              ariaLabel={`Реклама ${product.title}`}
                              onCommit={(advertisingPercent) =>
                                persistInputs(product, { advertisingPercent })
                              }
                            />
                            <Typography
                              variant="caption"
                              display="block"
                              color="text.secondary"
                            >
                              {money.format(metrics.advertisingSpend)} / шт.
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            {money.format(metrics.profitAfterAdvertising)}
                          </TableCell>
                          <TableCell align="right">
                            <Typography
                              color={
                                metrics.marginAfterAdvertising < 0
                                  ? "error.main"
                                  : "text.primary"
                              }
                              fontWeight={700}
                            >
                              {percent.format(metrics.marginAfterAdvertising)}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            {marketplaceProductUrl(product, view) && (
                              <Tooltip
                                title={
                                  view === "site"
                                    ? "Открыть товар на сайте"
                                    : `Открыть товар в ${marketplaceLabel(view)}`
                                }
                              >
                                <IconButton
                                  size="small"
                                  component="a"
                                  href={marketplaceProductUrl(product, view)}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  aria-label={
                                    view === "site"
                                      ? `Открыть товар ${product.title} на сайте`
                                      : `Открыть товар ${product.title} в ${marketplaceLabel(view)}`
                                  }
                                >
                                  <OpenInNewRoundedIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            )}
                            <Tooltip
                              title={
                                view === "site"
                                  ? `Эквайринг: ${money.format(metrics.acquiring)} · онлайн-чеки: ${money.format(metrics.onlineChecks)}`
                                  : `Объём ${decimal.format(metrics.volumeLiters)} л · комиссия ${money.format(metrics.commission)}`
                              }
                            >
                              <span>
                                <IconButton
                                  size="small"
                                  disabled={saving}
                                  onClick={() =>
                                    setSettings({
                                      product,
                                      inputs,
                                      view,
                                      offerId: marketplaceOfferId(
                                        product,
                                        view,
                                      ),
                                      productUrl: marketplaceProductUrl(
                                        product,
                                        view,
                                      ),
                                    })
                                  }
                                  aria-label={`Параметры расчёта ${product.title}`}
                                >
                                  <TuneRoundedIcon fontSize="small" />
                                </IconButton>
                              </span>
                            </Tooltip>
                          </TableCell>
                        </TableRow>
                      );
                    },
                  )}
                  {!rows.length && (
                    <TableRow>
                      <TableCell colSpan={12} align="center" sx={{ py: 6 }}>
                        {query
                          ? "Поиск не нашёл товаров"
                          : "В Strapi пока нет товаров"}
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      </Stack>

      <SettingsDialog
        state={settings}
        saving={settings ? savingId === settings.product.documentId : false}
        onClose={() => setSettings(null)}
        onSave={async (nextInputs, offerId, productUrl) => {
          const saved = await persistInputs(
            settings.product,
            nextInputs,
            settings.view,
            offerId,
            productUrl,
          );
          if (saved) {
            enqueueSnackbar(
              `Параметры ${VIEW_CONFIG[settings.view].label} сохранены`,
              {
                variant: "success",
              },
            );
            setSettings(null);
          }
        }}
      />
    </DashboardLayout>
  );
}
